import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import { ArrowUpTrayIcon, PlusIcon } from "@heroicons/react/24/outline"
import { Card, Dialog, Loader } from "~/ui-rtk/components/ui/common"
import { Button, Input, Select } from "~/ui-rtk/components/ui/controls"
import { H3, P } from "~/ui-rtk/components/ui/typography"
import { useTagCategories } from "~/ui-rtk/hooks/use-structured-tags"
import {
  TAG_RESERVED_SEPARATOR_HINT,
  type TagCategory,
} from "~/ui-rtk/types/structured-tags"
import CategoryCard from "./CategoryCard"
import ImportTaxonomyDialog from "./ImportTaxonomyDialog"

/**
 * Manage the taxonomy itself: which categories exist and which values each one
 * allows.
 *
 * This is the half of the tag system that had no screen. Values could be
 * created (inline, while tagging an ad) but never reviewed or retired, so
 * companies accumulate near-duplicates — `mold` alongside `mold_exposure` —
 * that split a creative's performance across two rows in every report built on
 * the taxonomy. Seeing all values of a category at once is the point.
 */

type PendingDelete =
  | { kind: "category"; category: TagCategory }
  | { kind: "option"; category: TagCategory; optionId: string; label: string }

type NewCategoryValueType = TagCategory["valueType"]

/** The four value types a category can have, in the order shown. */
const VALUE_TYPE_OPTIONS: Array<{
  value: NewCategoryValueType
  label: string
  hint: string
}> = [
  {
    value: "user_generated",
    label: "Open list",
    hint: "No seed list — values are created while tagging.",
  },
  {
    value: "predefined",
    label: "Predefined list",
    hint: "Curated list — add the values on the card after creating.",
  },
  {
    value: "mixed",
    label: "Mixed",
    hint: "Curated list, and users can add more while tagging.",
  },
  {
    value: "free_text",
    label: "Custom text",
    hint: `No value list — a custom string is typed per ad. Separator characters (${TAG_RESERVED_SEPARATOR_HINT}) are stripped so ad names stay parseable.`,
  },
]

/**
 * Review order for the categories list. System categories first (the six
 * canonical creative-taxonomy verticals the auto-tagger and the reports are
 * built on), then company-defined ones, alphabetical within each group.
 * Free-text categories always sink to the bottom: they have no value list to
 * review, so they are noise above the categories that do.
 */
export function compareCategoriesForReview(
  a: TagCategory,
  b: TagCategory,
): number {
  const aFreeText = a.valueType === "free_text"
  const bFreeText = b.valueType === "free_text"
  if (aFreeText !== bFreeText) return aFreeText ? 1 : -1
  if (a.isSystem !== b.isSystem) return a.isSystem ? -1 : 1
  return a.label.localeCompare(b.label)
}

export default function CategoriesTab() {
  const {
    categories,
    isLoading,
    createCategory,
    renameCategory,
    updateCategoryDescription,
    updateOptionDescription,
    deleteCategory,
    createOption,
    deleteOption,
  } = useTagCategories()

  const [newCategoryLabel, setNewCategoryLabel] = useState("")
  const [newCategoryValueType, setNewCategoryValueType] =
    useState<NewCategoryValueType>("user_generated")
  const [newCategoryAllowMultiple, setNewCategoryAllowMultiple] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)

  const sorted = useMemo(
    () => [...categories].sort(compareCategoriesForReview),
    [categories],
  )

  const handleCreateCategory = async () => {
    // The button disables itself while creating, but the Enter key path doesn't.
    if (isCreating) return
    const label = newCategoryLabel.trim()
    if (!label) return
    setIsCreating(true)
    try {
      await createCategory({
        label,
        valueType: newCategoryValueType,
        allowMultiple: newCategoryAllowMultiple,
      })
      setNewCategoryLabel("")
      setNewCategoryValueType("user_generated")
      setNewCategoryAllowMultiple(true)
      toast.success(`Added category "${label}"`)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not add the category",
      )
    } finally {
      setIsCreating(false)
    }
  }

  const confirmDelete = async () => {
    if (!pendingDelete) return
    setIsDeleting(true)
    try {
      if (pendingDelete.kind === "category") {
        await deleteCategory(pendingDelete.category.slug)
        toast.success(`Deleted "${pendingDelete.category.label}"`)
      } else {
        await deleteOption(pendingDelete.category.slug, pendingDelete.optionId)
        toast.success(`Deleted "${pendingDelete.label}"`)
      }
      setPendingDelete(null)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not delete that",
      )
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="py-16 flex justify-center">
        <Loader size={40} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-system-md">
      <div className="flex items-center justify-between gap-4">
        <P className="text-sm text-system-gray-400">
          Have the taxonomy in a spreadsheet? Import it as CSV — new categories
          and values are created, existing ones pick up the sheet&apos;s
          definitions, nothing is removed.
        </P>
        <Button
          variant={{ variant: "outlined" }}
          size="sm"
          type="button"
          className="shrink-0"
          onClick={() => setIsImportOpen(true)}
        >
          <ArrowUpTrayIcon className="w-4 h-4 mr-1" />
          Import CSV
        </Button>
      </div>

      {sorted.length === 0 ? (
        <Card className="py-12 text-center">
          <P className="text-system-gray-400">
            No tag categories yet. Add one below to start building the taxonomy.
          </P>
        </Card>
      ) : (
        sorted.map(category => (
          <CategoryCard
            key={category.id}
            category={category}
            onRename={label => renameCategory(category.slug, label)}
            onUpdateDescription={description =>
              updateCategoryDescription(category.slug, description)
            }
            onUpdateOptionDescription={(optionId, description) =>
              updateOptionDescription(category.slug, optionId, description)
            }
            onAddOption={label => createOption(category.slug, label)}
            onRequestDeleteOption={(optionId, label) =>
              setPendingDelete({ kind: "option", category, optionId, label })
            }
            onRequestDeleteCategory={() =>
              setPendingDelete({ kind: "category", category })
            }
          />
        ))
      )}

      <Card className="flex flex-col gap-2">
        <H3 className="text-white font-bold text-base">Add a category</H3>
        <P className="text-sm text-system-gray-400">
          A category is one vertical of the taxonomy — Hook, Style, Persona. Its
          values are added per category below.
        </P>
        <div className="flex items-end gap-2 mt-1">
          <Input
            value={newCategoryLabel}
            placeholder="e.g. Offer"
            onChange={e => setNewCategoryLabel(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") handleCreateCategory()
            }}
          />
          <Select
            aria-label="Value type"
            className="w-44 shrink-0"
            value={newCategoryValueType}
            onChange={e => {
              const next = e.target.value as NewCategoryValueType
              setNewCategoryValueType(next)
              // A per-ad typed string is naturally one value per ad; still
              // changeable via the Selection dropdown.
              if (next === "free_text") setNewCategoryAllowMultiple(false)
            }}
            options={VALUE_TYPE_OPTIONS.map(({ value, label }) => ({
              value,
              label,
            }))}
          />
          <Select
            aria-label="Selection"
            className="w-40 shrink-0"
            value={newCategoryAllowMultiple ? "multiple" : "single"}
            onChange={e =>
              setNewCategoryAllowMultiple(e.target.value === "multiple")
            }
            options={[
              { value: "multiple", label: "Multiple values" },
              { value: "single", label: "Single value" },
            ]}
          />
          <Button
            variant={{ variant: "solid", color: "pink" }}
            size="sm"
            type="button"
            isLoading={isCreating}
            disabled={!newCategoryLabel.trim() || isCreating}
            onClick={handleCreateCategory}
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add category
          </Button>
        </div>
        <P className="text-xs text-system-gray-500">
          {VALUE_TYPE_OPTIONS.find(o => o.value === newCategoryValueType)?.hint}
        </P>
      </Card>

      <ImportTaxonomyDialog
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
      />

      <Dialog
        isOpen={Boolean(pendingDelete)}
        handleClose={() => setPendingDelete(null)}
        width={440}
        classes={{
          container:
            "rounded-lg border border-basic-blue bg-basic-dark-blue text-left text-white shadow-xl",
        }}
      >
        {pendingDelete && (
          <div className="flex flex-col gap-3 p-system-md">
            <H3 className="text-white font-bold text-lg">
              Delete{" "}
              {pendingDelete.kind === "category"
                ? `category "${pendingDelete.category.label}"`
                : `value "${pendingDelete.label}"`}
              ?
            </H3>
            <P className="text-sm text-system-gray-300">
              {pendingDelete.kind === "category" ? (
                <>
                  Every ad tagged with any value of{" "}
                  <strong>{pendingDelete.category.label}</strong> loses that
                  tag, and reports grouped by it will show those ads as
                  untagged. If the naming convention uses this category, ad
                  names built from it will no longer parse.
                  {pendingDelete.category.isSystem &&
                    " This is a default category: once deleted it is also removed from AI tag suggestions, and only a taxonomy import brings it back."}
                </>
              ) : (
                <>
                  Ads currently tagged <strong>{pendingDelete.label}</strong>{" "}
                  lose that tag and will show as untagged until they are
                  re-tagged.
                </>
              )}
            </P>
            <div className="flex justify-end gap-2 mt-2">
              <Button
                variant={{ variant: "outlined" }}
                size="sm"
                type="button"
                onClick={() => setPendingDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant={{ variant: "solid", color: "red" }}
                size="sm"
                type="button"
                isLoading={isDeleting}
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                Delete
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  )
}
